import { api } from "@breakout/backend/convex/_generated/api"
import {
	buildPermit2TransferSigningPayload,
	generatePermit2Nonce,
	USDC_DECIMALS,
} from "@breakout/sdk"
import { deadlineSec } from "@breakout/shared/utils/time"
import { useWallets } from "@privy-io/react-auth"
import { useMutation } from "@tanstack/react-query"
import { useAction, useQuery } from "convex/react"
import { useCallback, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { Sentry } from "../../lib/sentry"
import { DEFAULT_DEADLINE_SECONDS } from "../../lib/tradeUtils"
import { createWalletClientFromWallet, findEmbeddedWallet } from "../../lib/wallet"
import { useInvalidateAfterTransaction } from "../trading/useInvalidateAfterTransaction"

export type WithdrawStatus = "idle" | "signing" | "processing" | "confirmed" | "error"

interface WithdrawParams {
	recipient: Address
	/** Amount of USDC to withdraw (human-readable, e.g., 10.50) */
	amount: number
}

interface WithdrawResult {
	success: boolean
	transactionHash: string
}

export function useWithdraw() {
	const config = useBreakoutConfig()
	const { wallets } = useWallets()
	const [status, setStatus] = useState<WithdrawStatus>("idle")
	const executeGaslessWithdraw = useAction(api.domains.trading.actions.executeGaslessWithdraw)
	const { invalidateAfterWithdraw } = useInvalidateAfterTransaction()

	// Fetch relayer address from backend (returns string, validated when used)
	const relayerAddress = useQuery(api.public.config.getRelayerAddress)

	const mutation = useMutation({
		mutationFn: async ({ recipient, amount }: WithdrawParams): Promise<WithdrawResult> => {
			const wallet = findEmbeddedWallet(wallets)
			if (!wallet) {
				throw new Error("No wallet connected")
			}

			if (!relayerAddress || !isAddress(relayerAddress)) {
				throw new Error("Relayer address not available")
			}
			const validatedRelayerAddress = getAddress(relayerAddress)

			const walletClient = await createWalletClientFromWallet({ wallet })
			if (!isAddress(wallet.address)) {
				throw new Error("Invalid wallet address")
			}
			const userAddress = getAddress(wallet.address)

			// Convert amount to USDC units (6 decimals)
			const amountInUnits = BigInt(Math.floor(amount * 10 ** USDC_DECIMALS))

			// Generate Permit2 nonce and deadline
			setStatus("signing")
			const permit2Nonce = generatePermit2Nonce()
			const deadline = BigInt(deadlineSec(DEFAULT_DEADLINE_SECONDS))

			// Build Permit2 transfer signing payload
			// The user signs permission for the relayer to transfer tokens
			const signingPayload = buildPermit2TransferSigningPayload(config.chainId, {
				permitted: {
					token: config.chainConfig.usdc,
					amount: amountInUnits,
				},
				spender: validatedRelayerAddress,
				nonce: permit2Nonce,
				deadline,
			})

			// Sign the Permit2 transfer
			const signature = await walletClient.signTypedData({
				account: userAddress,
				domain: signingPayload.domain,
				types: signingPayload.types,
				primaryType: signingPayload.primaryType,
				message: signingPayload.message,
			})

			// Execute gasless withdraw via backend
			setStatus("processing")
			const result = await executeGaslessWithdraw({
				recipient,
				amount: amountInUnits.toString(),
				permit: {
					token: config.chainConfig.usdc,
					amount: amountInUnits.toString(),
					nonce: permit2Nonce.toString(),
					deadline: deadline.toString(),
					signature,
				},
				owner: userAddress,
			})

			setStatus("confirmed")
			return {
				success: true,
				transactionHash: result.txHash,
			}
		},
		onSuccess: () => {
			// Invalidate balance with delayed refetches (handles RPC lag)
			const wallet = findEmbeddedWallet(wallets)
			if (wallet?.address && isAddress(wallet.address)) {
				const walletAddress = getAddress(wallet.address)
				invalidateAfterWithdraw({ userAddress: walletAddress })
			}
		},
		onError: error => {
			setStatus("error")
			console.error("[useWithdraw]", error)
			const wallet = findEmbeddedWallet(wallets)
			Sentry.captureException(error, {
				tags: { operation: "withdraw", domain: "wallet" },
				extra: { walletAddress: wallet?.address },
			})
		},
		onSettled: () => {
			setTimeout(() => setStatus("idle"), 2000)
		},
	})

	const reset = useCallback(() => {
		setStatus("idle")
		mutation.reset()
	}, [mutation])

	return {
		...mutation,
		status,
		reset,
		isReady: !!relayerAddress,
	}
}
